/**
 * <archive-calendar>: enhances the home page's server-rendered mini
 * calendar (internal/site/templates/home.templ's `archiveCalendar`).
 *
 * The templ output server-renders only the newest
 * `templates.PrerenderedMonths` months' grids up front (all but the latest
 * carrying the native `hidden` attribute), so the archive is fully usable
 * with no JS at all for that window: the latest month simply shows, and the
 * prev/next buttons are inert. Older months are not in the initial markup;
 * this component fetches them on demand as HTML fragments (rendered by
 * `templates.CalendarMonthFragment`, written to
 * dist/archive/months/YYYY-MM.html by generator.go) from the URL prefix in
 * `data-fragment-base`, so all rendering logic (tones, rounding, popover
 * placement) stays in Go - this file only fetches, inserts, and toggles.
 *
 * This component progressively enhances the server-rendered markup:
 *  - toggles `hidden` between month panels (fetching+inserting one first if
 *    it isn't already in the DOM) and updates the header label and
 *    prev/next disabled state (PC buttons, ArrowLeft/ArrowRight keys)
 *  - handles a horizontal swipe on the grid to change month on touch
 *    devices (swipe left -> newer, swipe right -> older)
 *  - on touch devices (which have no `:hover`), opens a meeting day's
 *    popover on first tap instead of navigating, and lets a second tap (or
 *    the popover's own "詳細を見る" link) navigate normally - including for
 *    a panel fetched on demand
 *
 * A light-DOM custom element (createRenderRoot returns `this`, and render()
 * is never overridden/called for content) is used deliberately instead of
 * Lit's usual shadow-DOM templating: the whole point is to keep the
 * server-rendered children exactly as they are and just wire up behavior
 * on top, the same "enhance what's already there" spirit as
 * release-panel.ts/release-toc.ts (which use plain functions instead of a
 * custom element only because they don't need per-instance component
 * lifecycle/state the way month navigation here does).
 */
import { LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

/** Minimum horizontal drag distance (px) to register as a swipe. */
const SWIPE_THRESHOLD_PX = 40;
/** Month panels are contiguous, so a year jump is 12 months. */
const MONTHS_PER_YEAR = 12;

interface PointerStart {
  x: number;
  y: number;
  id: number;
}

/** Parses a "YYYY-MM" month key into its numeric parts. */
function parseMonthKey(key: string): { year: number; month: number } {
  const [year, month] = key.split('-').map(Number);
  return { year, month };
}

/** Formats a year and an arbitrary (possibly out-of-range) 1-based month back into a normalized "YYYY-MM" key. */
function formatMonthKey(year: number, month: number): string {
  const normalizedYear = year + Math.floor((month - 1) / 12);
  const normalizedMonth = ((((month - 1) % 12) + 12) % 12) + 1;
  return `${normalizedYear}-${String(normalizedMonth).padStart(2, '0')}`;
}

/** Shifts a "YYYY-MM" month key by delta months (may be negative). */
function addMonths(key: string, delta: number): string {
  const { year, month } = parseMonthKey(key);
  return formatMonthKey(year, month + delta);
}

/** Clamps a "YYYY-MM" month key into [min, max] (lexicographic order matches chronological order for this zero-padded format). */
function clampMonthKey(key: string, min: string, max: string): string {
  if (key < min) return min;
  if (key > max) return max;
  return key;
}

@customElement('archive-calendar')
export class ArchiveCalendar extends LitElement {
  private panelsByKey = new Map<string, HTMLElement>();
  private pendingFetches = new Map<string, Promise<HTMLElement | null>>();
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private prevYearButton: HTMLButtonElement | null = null;
  private nextYearButton: HTMLButtonElement | null = null;
  private monthLabel: HTMLElement | null = null;
  private panelsContainer: HTMLElement | null = null;
  private fragmentBase = '';
  private currentKey = '';
  private oldestKey = '';
  private newestKey = '';
  /**
   * Bumped on every navigate() call; a fetch's result is only applied if
   * this still matches the token captured when that fetch started, so a
   * slower, superseded navigation can never clobber a later one ("last
   * navigation wins").
   */
  private navToken = 0;
  private pointerStart: PointerStart | null = null;
  private suppressNextClick = false;
  private lastPointerType = 'mouse';

  /**
   * Render into light DOM so the server-rendered month panels stay put;
   * this component never calls render() to produce content of its own.
   */
  protected createRenderRoot(): this {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.fragmentBase = this.dataset.fragmentBase ?? '';
    this.prevButton = this.querySelector<HTMLButtonElement>('.cal-nav-prev');
    this.nextButton = this.querySelector<HTMLButtonElement>('.cal-nav-next');
    this.prevYearButton = this.querySelector<HTMLButtonElement>('.cal-nav-prev-year');
    this.nextYearButton = this.querySelector<HTMLButtonElement>('.cal-nav-next-year');
    this.monthLabel = this.querySelector<HTMLElement>('.cal-month-label');
    this.panelsContainer = this.querySelector<HTMLElement>('.cal-panels');

    const panels = Array.from(this.querySelectorAll<HTMLElement>('[data-month]'));
    panels.forEach((panel) => {
      const key = panel.dataset.month;
      if (!key) return;
      this.panelsByKey.set(key, panel);
      this.wireMeetingDayLinks(panel);
    });

    if (this.panelsByKey.size === 0) return;

    // Prefer the server-computed full range (data-oldest-month/
    // data-newest-month), which may extend beyond the panels actually
    // present in the DOM; fall back to deriving it from the prerendered
    // panels for markup that predates these attributes.
    const presentKeys = Array.from(this.panelsByKey.keys()).sort();
    this.oldestKey = this.dataset.oldestMonth || presentKeys[0];
    this.newestKey = this.dataset.newestMonth || presentKeys[presentKeys.length - 1];

    const visible = panels.find((panel) => !panel.hidden);
    this.currentKey = visible?.dataset.month ?? this.newestKey;

    this.prevButton?.addEventListener('click', () => this.navigate(-1));
    this.nextButton?.addEventListener('click', () => this.navigate(1));
    this.prevYearButton?.addEventListener('click', () => this.navigate(-MONTHS_PER_YEAR));
    this.nextYearButton?.addEventListener('click', () => this.navigate(MONTHS_PER_YEAR));

    this.addEventListener('keydown', this.handleKeyDown);

    if (this.panelsContainer) {
      this.panelsContainer.addEventListener('pointerdown', this.handlePointerDown);
      this.panelsContainer.addEventListener('pointerup', this.handlePointerUp);
      this.panelsContainer.addEventListener('pointercancel', this.handlePointerCancel);
      this.panelsContainer.addEventListener('click', this.handleClickCapture, true);
    }

    document.addEventListener('click', this.handleDocumentClick);

    this.updateButtons();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  /**
   * Moves the visible month by delta months: -1/+1 for the prev/next
   * buttons and ArrowLeft/ArrowRight, ±MONTHS_PER_YEAR for the year-jump
   * buttons. The target is clamped to [oldestKey, newestKey], so a year
   * jump near either end lands on that end month; a delta that doesn't
   * change the effective target is a no-op.
   *
   * When the target panel is already in the DOM (cached in panelsByKey),
   * switching is fully synchronous - no fetch, no microtask delay.
   * Otherwise it's fetched (see fetchAndShow); navToken ensures only the
   * most recently requested navigation can end up visible, and a fetch
   * failure simply leaves the current month showing (buttons stay enabled
   * so the user can retry).
   */
  private navigate(delta: number): void {
    if (!this.currentKey || !this.oldestKey || !this.newestKey) return;

    const target = clampMonthKey(addMonths(this.currentKey, delta), this.oldestKey, this.newestKey);
    if (target === this.currentKey) return;

    const token = ++this.navToken;
    const cached = this.panelsByKey.get(target);
    if (cached) {
      this.showPanel(target, cached);
      this.prefetchNeighbors(target);
      return;
    }

    void this.fetchAndShow(target, token);
  }

  private async fetchAndShow(key: string, token: number): Promise<void> {
    const panel = await this.getOrFetchPanel(key);
    if (token !== this.navToken) return; // superseded by a newer navigation
    if (!panel) return; // fetch failed; stay on the current month

    this.showPanel(key, panel);
    this.prefetchNeighbors(key);
  }

  /**
   * Prefetches the immediate ±1 month neighbors of key in the background
   * (only if missing and within range), so a follow-up swipe/click feels
   * instant. Year jumps are deliberately not prefetched.
   */
  private prefetchNeighbors(key: string): void {
    for (const delta of [-1, 1]) {
      const neighbor = addMonths(key, delta);
      if (neighbor < this.oldestKey || neighbor > this.newestKey) continue;
      if (this.panelsByKey.has(neighbor)) continue;
      void this.getOrFetchPanel(neighbor);
    }
  }

  /**
   * Returns the panel for key, fetching it if necessary. Concurrent
   * requests for the same key share one in-flight fetch (cached in
   * pendingFetches), so rapid clicks/prefetches never double-fetch.
   */
  private getOrFetchPanel(key: string): Promise<HTMLElement | null> {
    const cached = this.panelsByKey.get(key);
    if (cached) return Promise.resolve(cached);

    const pending = this.pendingFetches.get(key);
    if (pending) return pending;

    const promise = this.fetchPanel(key).finally(() => {
      this.pendingFetches.delete(key);
    });
    this.pendingFetches.set(key, promise);
    return promise;
  }

  /**
   * Fetches `${fragmentBase}${key}.html`, extracts its `[data-month]`
   * panel, and inserts it into the DOM (still hidden, wired up like any
   * other panel). Returns null on any failure (no fragmentBase configured,
   * network error, non-ok response, or missing panel in the response) -
   * logged via console.warn only, never thrown, so callers can just treat
   * it as "couldn't navigate there right now".
   */
  private async fetchPanel(key: string): Promise<HTMLElement | null> {
    if (!this.fragmentBase) return null;

    try {
      const res = await fetch(`${this.fragmentBase}${key}.html`);
      if (!res.ok) {
        throw new Error(`fetch failed with status ${res.status}`);
      }
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const panel = doc.querySelector<HTMLElement>(`[data-month="${key}"]`);
      if (!panel) {
        throw new Error('month panel not found in fetched fragment');
      }

      const imported = document.importNode(panel, true) as HTMLElement;
      this.insertPanel(key, imported);
      return imported;
    } catch (err) {
      console.warn(`archive-calendar: failed to load month ${key}`, err);
      return null;
    }
  }

  private insertPanel(key: string, panel: HTMLElement): void {
    panel.hidden = true;
    this.wireMeetingDayLinks(panel);
    this.panelsContainer?.appendChild(panel);
    this.panelsByKey.set(key, panel);
  }

  private showPanel(key: string, panel: HTMLElement): void {
    const current = this.panelsByKey.get(this.currentKey);
    if (current && current !== panel) current.hidden = true;
    panel.hidden = false;
    this.currentKey = key;

    this.closeAllPopovers();
    this.updateButtons();
  }

  private updateButtons(): void {
    const atNewest = this.currentKey === this.newestKey;
    const atOldest = this.currentKey === this.oldestKey;
    if (this.nextButton) this.nextButton.disabled = atNewest;
    if (this.nextYearButton) this.nextYearButton.disabled = atNewest;
    if (this.prevButton) this.prevButton.disabled = atOldest;
    if (this.prevYearButton) this.prevYearButton.disabled = atOldest;

    const current = this.panelsByKey.get(this.currentKey);
    if (this.monthLabel && current) this.monthLabel.textContent = current.dataset.monthLabel ?? '';
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(1);
    }
  };

  private handlePointerDown = (event: PointerEvent): void => {
    this.lastPointerType = event.pointerType;
    this.suppressNextClick = false;
    this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
  };

  private handlePointerUp = (event: PointerEvent): void => {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start || start.id !== event.pointerId) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      // Swipe left (dx < 0) reveals a newer month; swipe right reveals an
      // older one.
      this.navigate(dx < 0 ? 1 : -1);
      // A mouse drag that starts and ends on the same band link would
      // otherwise also fire a click and navigate away.
      this.suppressNextClick = true;
    }
  };

  private handleClickCapture = (event: MouseEvent): void => {
    if (!this.suppressNextClick) return;
    this.suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  private handlePointerCancel = (): void => {
    this.pointerStart = null;
  };

  /**
   * Wires up tap-to-open-popover / lastPointerType tracking for every
   * meeting-day link within panel. Applied to the initially server-rendered
   * panels in connectedCallback, and again to each panel fetched on demand
   * (insertPanel), so both get identical behavior.
   */
  private wireMeetingDayLinks(panel: HTMLElement): void {
    panel.querySelectorAll<HTMLAnchorElement>('a[data-meeting-day]').forEach((link) => {
      link.addEventListener('pointerdown', (event) => {
        this.lastPointerType = event.pointerType;
      });
      link.addEventListener('click', (event) => this.handleMeetingDayClick(event, link));
    });
  }

  /**
   * On touch devices (no `:hover`), the first tap on a meeting day opens
   * its popover instead of navigating; a second tap (popover already open)
   * or any pointer type other than touch navigates normally. The
   * popover's own "詳細を見る" link is untouched and always navigates.
   */
  private handleMeetingDayClick(event: MouseEvent, link: HTMLAnchorElement): void {
    const pointerType = (event as PointerEvent).pointerType || this.lastPointerType;
    if (pointerType !== 'touch') return;

    const popover = link.parentElement?.querySelector<HTMLElement>('[data-week-popover]');
    if (!popover) return;

    if (popover.hasAttribute('data-open')) {
      // Second tap: let the click navigate.
      return;
    }

    event.preventDefault();
    this.closeAllPopovers();
    popover.setAttribute('data-open', 'true');
  }

  private handleDocumentClick = (event: MouseEvent): void => {
    const openPopover = this.querySelector<HTMLElement>('[data-week-popover][data-open]');
    if (!openPopover) return;

    const cell = openPopover.closest('.cal-meeting-cell');
    const path = event.composedPath();
    if (cell && path.includes(cell)) return;

    this.closeAllPopovers();
  };

  private closeAllPopovers(): void {
    this.querySelectorAll<HTMLElement>('[data-week-popover][data-open]').forEach((popover) => {
      popover.removeAttribute('data-open');
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'archive-calendar': ArchiveCalendar;
  }
}
